import { ErrorApi } from '../api/problema';

/**
 * Estados de una página del panel (0.4): vacía, cargando, error, sin permiso y bloque no contratado. El «solo lectura»
 * no sustituye a la página: es el chip «Solo lectura» y la franja roja con su motivo (modo del negocio,
 * `listEntitlements` / `getMyNavigation`; ver `franjas.ts`). Intentar escribir en solo lectura (`read_only_mode`) tampoco
 * cambia la página: es un aviso breve (`avisoBreveDeError`).
 */
export type TipoEstado = 'vacia' | 'cargando' | 'error' | 'sin-permiso' | 'no-contratado';

export interface EstadoDeError {
    tipo: 'error' | 'sin-permiso' | 'no-contratado';
    /** Código para soporte (`correlation_id` del Problem), si lo hay. */
    codigoSoporte: string | null;
}

/** Qué estado pinta la página cuando la API responde con un error (por su código estable, no por el texto). */
export function estadoDeError(error: unknown): EstadoDeError {
    if (error instanceof ErrorApi) {
        const codigoSoporte = error.problema.correlation_id ?? null;
        if (error.codigo === 'permission_denied' || error.codigo === 'tenant_mismatch') {
            return { tipo: 'sin-permiso', codigoSoporte };
        }
        if (error.codigo === 'entitlement_required') {
            return { tipo: 'no-contratado', codigoSoporte };
        }
        return { tipo: 'error', codigoSoporte };
    }
    return { tipo: 'error', codigoSoporte: null };
}

/** Estados que se pueden forzar con `?estado=` para revisar el diseño (solo con datos simulados, como el prototipo). */
export const ESTADOS_REVISABLES: readonly TipoEstado[] = ['vacia', 'cargando', 'error', 'sin-permiso', 'no-contratado'];

export function esEstadoRevisable(valor: string | null): valor is TipoEstado {
    return valor !== null && (ESTADOS_REVISABLES as readonly string[]).includes(valor);
}

/**
 * Errores que no cambian la página sino que se cuentan con un aviso breve (0.3): intentar escribir con el negocio en solo
 * lectura (`read_only_mode`, G70, G114). El texto es el que manda la API en el `Problem` (el diseño no da uno propio).
 * null si el error no es de este tipo (entonces va a su estado de página, `estadoDeError`).
 */
export function avisoBreveDeError(error: unknown): string | null {
    if (error instanceof ErrorApi && error.codigo === 'read_only_mode') {
        return error.problema.detail ?? error.problema.title;
    }
    return null;
}
